import { FC } from 'react';
import { IonButton } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { CommunityModelFactory } from '../CommunityModelFactory';
import { SharingAndAccessState } from './SharingAndAccessState';
import { Text } from '../../components/Text';

// Shown at the top of a care-map member's edit drawer when that member is linked to a
// collaborator. Makes the access relationship explicit and links back to Sharing & Access.
// Renders nothing when the member isn't linked. Takes the member's contact email +
// linkedUserId (not just the node id) so the link survives delete-and-re-add, which
// regenerates the node id but not the email/account identity (see findCollaboratorForMember).

const useStyles = createUseStyles({
    banner: {
        background: 'rgba(17,120,157,0.08)',
        border: '1px solid rgba(17,120,157,0.18)',
        borderRadius: 10,
        padding: '8px 8px 8px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        margin: '4px 0 8px'
    },
    text: {
        flex: 1
    }
});

export const MemberAccessBanner: FC<{ memberId?: string; email?: string; linkedUserId?: string }> = ({ memberId, email, linkedUserId }) => {
    const classes = useStyles();

    const factory = useInstance(CommunityModelFactory);
    useSubscription(() => factory);
    const community = factory.forActiveCaremap();

    const sharingState = useInstance(SharingAndAccessState);

    if (!community.isLoaded()) {
        return null;
    }

    const link = community.findCollaboratorForMember({ memberId, email, linkedUserId });
    if (!link) {
        return null;
    }

    const roleLabel = link.access === 'co-owner' ? 'Co-Owner' : 'Viewer';

    return (
        <div className={classes.banner} data-id="member-access-banner">
            <div className={classes.text}>
                <Text type="detail">
                    <strong className="mp-mask">{link.name || 'This person'}</strong> has{' '}
                    <strong>{roleLabel}</strong> access{link.isPending ? ' (invite pending)' : ''}.
                </Text>
            </div>
            <IonButton fill="clear" size="small" onClick={() => sharingState.open('Care Map Member Detail')}>
                Manage
            </IonButton>
        </div>
    );
};
